// OrdersPage.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'
import {
  ChevronLeft,
  ShoppingCart,
  Receipt,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  DollarSign,
  Calendar,
  Filter,
  Search,
  Download,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Printer,
  Send,
  Copy,
  FileText,
  TrendingUp,
  Package,
  Maximize,
  User,
  MapPin,
  Phone,
  Tag,
  Percent,
  Ban,
  RotateCcw,
  Utensils,
  Layers,
  AlertTriangle,
  HardDrive,
  Briefcase,
  Coffee,
  Home,
  Truck,
  MessageSquare,
  CreditCard as CardIcon,
  Wallet,
  Gift,
  RotateCw,
  MoreHorizontal,
  ChevronDown,
  ExternalLink
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

// Custom Components
import { ErrorBoundary } from '../components/ui/ErrorBoundary'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DataTable, Column } from '../components/ui/DataTable'
import { StatusBadge } from '../components/ui/StatusBadge'
import { CurrencyDisplay } from '../components/ui/CurrencyDisplay'
import { DateRangePicker } from '../components/ui/DateRangePicker'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { Pagination } from '../components/ui/Pagination'
import { OrderDetailsModal } from '../components/orders/OrderDetailsModal'

// Types (aligned with schema.ts)
interface Order {
  id: string
  order_number: string
  order_date: string
  customer_id?: string
  user_id?: string
  cash_register_id?: string
  business_day_id?: string
  table_id?: string
  room_id?: string
  status: 'draft' | 'open' | 'parked' | 'completed' | 'voided' | 'refunded'
  order_type: 'sale' | 'return' | 'exchange' | 'quote' | 'refund'
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  change_amount: number
  is_parked: boolean
  is_locked: boolean
  is_voided: boolean
  void_reason_id?: string
  voided_by?: string
  voided_date?: string
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded'
  payment_status_date?: string
  notes?: string
  created_at: string
  updated_at: string
  fiscal_receipt_number?: string
  fiscal_receipt_date?: string
  delivery_address?: string
  delivery_date?: string
  delivery_time?: string
  delivery_status?: string
  delivery_driver_id?: string
  delivery_fee: number
  total_tips: number
  service_charge: number
  customer_count: number
  table_number?: string
  waiter_id?: string
  kitchen_status?: string
  preparation_time?: number
  source_channel?: string
  sales_channel?: string
  guest_count: number
  special_instructions?: string
  is_gift: boolean
  gift_wrap_fee: number
  rounding_adjustment: number
  points_earned: number
  points_redeemed: number
  points_balance_after: number
  affiliate_id?: string
  ip_address?: string
  user_agent?: string

  // Relations
  customer?: Customer
  user?: User
  table?: Table
  room?: Room
  waiter?: User
  delivery_driver?: Driver
  business_day?: BusinessDay
  cash_register?: CashRegister
  order_items?: OrderItem[]
  order_payments?: OrderPayment[]
  order_rounds?: OrderRound[]
  commissions?: Commission[]
  promotions?: Promotion[]
  fiscal_document?: FiscalDocument
}

interface Customer {
  id: string
  code: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone?: string
  mobile?: string
  customer_type: 'individual' | 'company'
  loyalty_points_balance: number
  vip_status: boolean
  blacklisted: boolean
  total_lifetime_value: number
  average_order_value: number
  visit_count: number
  last_visit_date?: string
}

interface User {
  id: string
  username: string
  first_name?: string
  last_name?: string
  role: string
  is_active: boolean
  authorization_level: number
}

interface Table {
  id: string
  name: string
  capacity: number
  room_id: string
  is_active: boolean
}

interface Room {
  id: string
  name: string
  store_id: string
  sort_order: number
}

interface Driver {
  id: string
  driver_code: string
  first_name: string
  last_name: string
  phone?: string
  status: string
}

interface BusinessDay {
  id: string
  opened_date: string
  closed_date?: string
  status: string
  total_sales: number
  total_transactions: number
}

interface CashRegister {
  id: string
  name: string
  code: string
  terminal_id?: string
  is_active: boolean
}

interface OrderItem {
  id: string
  order_id: string
  product_id?: string
  product_code?: string
  product_name: string
  line_number: number
  parent_line_number?: number
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  discount_type?: string
  tax_percent: number
  tax_amount: number
  total_amount: number
  serial_number?: string
  lot_number?: string
  return_quantity: number
  weight_sold?: number
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  kitchen_status?: string
  round_number: number
  is_confirmed: boolean
  is_canceled: boolean
  is_voided: boolean
  voided_by?: string
  voided_date?: string
  notes?: string
  created_at: string
  updated_at: string
  cost_price?: number
  preparation_status?: string
  prepared_by?: string
  prepared_at?: string
  served_by?: string
  served_at?: string
  allergen_warnings?: string
  modifier_total: number

  // Relations
  product?: Product
  order_item_modifiers?: OrderItemModifier[]
  order_item_taxes?: OrderItemTax[]
  order_item_rounds?: OrderItemRound[]
}

interface Product {
  id: string
  code: string
  name: string
  description?: string
  price: number
  cost_price?: number
  category_id?: string
  is_service: boolean
  track_inventory: boolean
  is_active: boolean
  allow_decimal_quantities: boolean
  stock_control_type?: string
  is_modifier: boolean
  modifiers_allowed: boolean
  print_kitchen_receipt: boolean
  preparation_time_minutes?: number
  kitchen_printer_id?: string
}

interface OrderItemModifier {
  id: string
  order_item_id: string
  modifier_option_id: string
  quantity: number
  price_adjustment: number
  created_at: string

  modifier_option?: ModifierOption
}

interface ModifierOption {
  id: string
  name: string
  price_adjustment: number
  is_active: boolean
  sort_order: number
  modifier_group_id: string
}

interface OrderItemTax {
  id: string
  order_item_id: string
  tax_rate_id: string
  tax_amount: number
  created_at: string

  tax_rate?: TaxRate
}

interface TaxRate {
  id: string
  name: string
  code: string
  rate: number
  is_active: boolean
}

interface OrderItemRound {
  order_item_id: string
  order_round_id: string
  quantity: number
}

interface OrderPayment {
  id: string
  order_id: string
  payment_type_id: string
  amount: number
  tendered_amount?: number
  change_amount: number
  payment_date: string
  reference_number?: string
  card_type?: string
  card_last_four?: string
  authorization_code?: string
  terminal_id?: string
  is_voided: boolean
  void_reason_id?: string
  voided_by?: string
  voided_at?: string
  notes?: string

  // Relations
  payment_type?: PaymentType
  void_reason?: VoidReason
}

interface PaymentType {
  id: string
  name: string
  code: string
  is_active: boolean
  opens_cash_drawer: boolean
  requires_reference: boolean
  allows_change: boolean
  is_default: boolean
}

interface VoidReason {
  id: string
  name: string
  description?: string
  is_active: boolean
}

interface OrderRound {
  id: string
  order_id: string
  round_number: number
  created_at: string

  order_item_rounds?: OrderItemRound[]
}

interface Commission {
  id: string
  employee_id: string
  order_id: string
  commission_date: string
  commission_type?: string
  base_amount: number
  commission_rate: number
  commission_amount: number
  status: string
  paid_date?: string
}

interface Promotion {
  id: string
  code?: string
  name: string
  promotion_type: string
  discount_percent?: number
  discount_amount?: number
  start_date: string
  end_date: string
  is_active: boolean
}

interface FiscalDocument {
  id: string
  document_number: string
  document_type: string
  order_id: string
  cash_register_id: string
  issue_date: string
  total_amount: number
  tax_amount: number
  signature?: string
  is_voided: boolean
}

interface VoidedItem {
  id: string
  order_id: string
  order_item_id?: string
  product_id?: string
  product_name: string
  quantity: number
  unit_price: number
  total_amount: number
  void_reason_id?: string
  void_reason_text?: string
  voided_by: string
  voided_date: string
  notes?: string

  // Relations
  void_reason?: VoidReason
  product?: Product
}

interface OrderAnalytics {
  totalOrders: number
  totalSales: number
  totalRefunds: number
  averageOrderValue: number
  totalTax: number
  totalDiscount: number
  totalTips: number
  totalServiceCharge: number
  totalDeliveryFee: number
  completedOrders: number
  pendingOrders: number
  voidedOrders: number
  cashSales: number
  cardSales: number
  creditSales: number
  giftCardSales: number
  otherSales: number
  totalCustomers: number
  newCustomers: number
  returningCustomers: number
  busiestHour: string
  averagePreparationTime: number
  averageServiceTime: number
  topProducts: Array<{
    product_id: string
    product_name: string
    quantity: number
    total_amount: number
  }>
  topCategories: Array<{
    category_id: string
    category_name: string
    total_amount: number
  }>
  paymentMethodBreakdown: Array<{
    payment_type: string
    count: number
    total_amount: number
    percentage: number
  }>
  hourlySales: Array<{
    hour: number
    total_amount: number
    order_count: number
  }>
  dailySales: Array<{
    date: string
    total_amount: number
    order_count: number
  }>
}

type TabType = 
  | 'orders'
  | 'items'
  | 'payments'
  | 'rounds'
  | 'voided'
  | 'analytics'

type OrderSubTab = 'all' | 'draft' | 'open' | 'parked' | 'completed' | 'voided' | 'refunded'
type PaymentSubTab = 'all' | 'cash' | 'card' | 'credit' | 'giftcard' | 'other'
type ItemSubTab = 'all' | 'confirmed' | 'pending' | 'canceled' | 'voided'
type DeliverySubTab = 'all' | 'pending' | 'assigned' | 'in_transit' | 'delivered' | 'failed'

const ITEMS_PER_PAGE = 50
const DEBOUNCE_DELAY = 500
const AUTO_REFRESH_INTERVAL = 30000 // 30 seconds

export default function OrdersPage() {
  useAuthStore()
  const [activeTab, setActiveTab] = useState<TabType>('orders')
  const [activeSubTab, setActiveSubTab] = useState<any>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({})

  // Data States
  const [orders, setOrders] = useState<Order[]>([])
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [orderPayments, setOrderPayments] = useState<OrderPayment[]>([])
  const [orderRounds, setOrderRounds] = useState<OrderRound[]>([])
  const [voidedItems, setVoidedItems] = useState<VoidedItem[]>([])
  const [analytics, setAnalytics] = useState<OrderAnalytics>({
    totalOrders: 0,
    totalSales: 0,
    totalRefunds: 0,
    averageOrderValue: 0,
    totalTax: 0,
    totalDiscount: 0,
    totalTips: 0,
    totalServiceCharge: 0,
    totalDeliveryFee: 0,
    completedOrders: 0,
    pendingOrders: 0,
    voidedOrders: 0,
    cashSales: 0,
    cardSales: 0,
    creditSales: 0,
    giftCardSales: 0,
    otherSales: 0,
    totalCustomers: 0,
    newCustomers: 0,
    returningCustomers: 0,
    busiestHour: '',
    averagePreparationTime: 0,
    averageServiceTime: 0,
    topProducts: [],
    topCategories: [],
    paymentMethodBreakdown: [],
    hourlySales: [],
    dailySales: []
  })

  // UI State
  const [searchTerm, setSearchTerm] = useState('')
  const [filterOptions, setFilterOptions] = useState<any>({
    status: '',
    payment_status: '',
    order_type: '',
    date_from: '',
    date_to: '',
    customer_id: '',
    user_id: '',
    min_amount: '',
    max_amount: '',
    delivery_status: '',
    kitchen_status: '',
    source_channel: '',
    sales_channel: ''
  })
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [exporting, setExporting] = useState(false)

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm || Object.values(filterOptions).some(v => v !== '' && v !== null)) {
        fetchData()
      }
    }, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [searchTerm, filterOptions])

  // Auto-refresh for open orders
  useEffect(() => {
    if (activeTab === 'orders' && activeSubTab === 'open') {
      const interval = setInterval(() => {
        fetchOrdersData()
      }, AUTO_REFRESH_INTERVAL)

      return () => clearInterval(interval)
    }
  }, [activeTab, activeSubTab])

  // Fetch data on tab/subtab change
  useEffect(() => {
    fetchData()
  }, [activeTab, activeSubTab, currentPage])

  /**
   * Main data fetching orchestrator with error handling
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      switch (activeTab) {
        case 'orders':
          await fetchOrdersData()
          break
        case 'items':
          await fetchOrderItemsData()
          break
        case 'payments':
          await fetchPaymentsData()
          break
        case 'rounds':
          await fetchRoundsData()
          break
        case 'voided':
          await fetchVoidedItemsData()
          break
        case 'analytics':
          await fetchAnalyticsData()
          break
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm, filterOptions, dateRange])

  const fetchOrdersData = async () => {
    const params: any = {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: searchTerm,
      ...filterOptions
    }

    if (activeSubTab !== 'all') {
      params.status = activeSubTab
    }

    if (dateRange[0]) {
      params.date_from = format(dateRange[0], 'yyyy-MM-dd')
    }
    if (dateRange[1]) {
      params.date_to = format(dateRange[1], 'yyyy-MM-dd')
    }

    const response = await apiClient.get('/api/orders', { params })
    setOrders(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length)
  }

  const fetchOrderItemsData = async () => {
    const params: any = {
      page: currentPage,
      limit: ITEMS_PER_PAGE
    }

    if (activeSubTab === 'confirmed') {
      params.is_confirmed = 'true'
    } else if (activeSubTab === 'pending') {
      params.is_confirmed = 'false'
      params.is_canceled = 'false'
      params.is_voided = 'false'
    } else if (activeSubTab === 'canceled') {
      params.is_canceled = 'true'
    } else if (activeSubTab === 'voided') {
      params.is_voided = 'true'
    }

    const response = await apiClient.get('/api/orders/items', { params })
    setOrderItems(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length)
  }

  const fetchPaymentsData = async () => {
    const params: any = {
      page: currentPage,
      limit: ITEMS_PER_PAGE
    }

    if (activeSubTab !== 'all') {
      params.payment_type = activeSubTab
    }

    const response = await apiClient.get('/api/orders/payments', { params })
    setOrderPayments(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length)
  }

  const fetchRoundsData = async () => {
    const params = {
      page: currentPage,
      limit: ITEMS_PER_PAGE
    }
    const response = await apiClient.get('/api/orders/rounds', { params })
    setOrderRounds(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length)
  }

  const fetchVoidedItemsData = async () => {
    const params = {
      page: currentPage,
      limit: ITEMS_PER_PAGE
    }
    const response = await apiClient.get('/api/orders/voided-items', { params })
    setVoidedItems(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length)
  }

  const fetchAnalyticsData = async () => {
    const params: any = {}
    if (dateRange[0]) {
      params.date_from = format(dateRange[0], 'yyyy-MM-dd')
    }
    if (dateRange[1]) {
      params.date_to = format(dateRange[1], 'yyyy-MM-dd')
    }
    const response = await apiClient.get('/api/orders/analytics', { params })
    setAnalytics(response.data)
  }

  const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
    setExporting(true)
    try {
      const response = await apiClient.get(`/api/orders/export/${format}`, {
        params: { ...filterOptions, date_from: dateRange[0]?.toISOString(), date_to: dateRange[1]?.toISOString() },
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `orders_${format}_${new Date().toISOString().split('T')[0]}.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success(`Exported successfully as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Export failed')
      console.error('Export error:', error)
    } finally {
      setExporting(false)
    }
  }

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props)
    setShowConfirmDialog(true)
  }

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false)
    setConfirmDialogProps({})
  }

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm()
    }
    closeConfirmDialog()
  }

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order)
    setShowOrderDetails(true)
  }

  const handleVoidOrder = async (orderId: string, reason: string) => {
    try {
      await apiClient.patch(`/api/orders/${orderId}/void`, { reason })
      toast.success('Order voided successfully')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to void order')
    }
  }

  const handleRefundOrder = async (orderId: string, data: any) => {
    try {
      await apiClient.post(`/api/orders/${orderId}/refund`, data)
      toast.success('Refund processed successfully')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to process refund')
    }
  }

  const handleReopenOrder = async (orderId: string) => {
    try {
      await apiClient.patch(`/api/orders/${orderId}/reopen`)
      toast.success('Order reopened successfully')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to reopen order')
    }
  }

  const handlePrintReceipt = async (orderId: string, copyType: 'customer' | 'kitchen' | 'fiscal') => {
    try {
      const response = await apiClient.get(`/api/orders/${orderId}/print/${copyType}`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `receipt_${orderId}_${copyType}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success(`${copyType.charAt(0).toUpperCase() + copyType.slice(1)} receipt printed`)
    } catch (error) {
      toast.error('Failed to print receipt')
    }
  }

  const handleSendReceipt = async (orderId: string, method: 'email' | 'sms') => {
    try {
      await apiClient.post(`/api/orders/${orderId}/send-receipt`, { method })
      toast.success(`Receipt sent via ${method.toUpperCase()}`)
    } catch (error) {
      toast.error(`Failed to send ${method} receipt`)
    }
  }

  const handleSplitOrder = async (orderId: string, splitData: any) => {
    try {
      await apiClient.post(`/api/orders/${orderId}/split`, splitData)
      toast.success('Order split successfully')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to split order')
    }
  }

  const handleMergeOrders = async (orderIds: string[]) => {
    try {
      await apiClient.post('/api/orders/merge', { orderIds })
      toast.success('Orders merged successfully')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to merge orders')
    }
  }

  const handleTransferTable = async (orderId: string, tableId: string) => {
    try {
      await apiClient.patch(`/api/orders/${orderId}/transfer-table`, { tableId })
      toast.success('Order transferred to new table')
      fetchOrdersData()
    } catch (error) {
      toast.error('Failed to transfer order')
    }
  }

  /**
   * Order Columns
   */
  const orderColumns: Column<Order>[] = useMemo(() => [
    {
      key: 'order_number',
      header: 'Order #',
      accessor: (row) => row.order_number,
      sortable: true,
      filterable: true,
      width: '120px',
      className: 'font-mono font-semibold',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="font-mono font-semibold">{value}</span>
          {row.fiscal_receipt_number && (
            <span className="text-xs text-gray-500">Fiscal: {row.fiscal_receipt_number}</span>
          )}
        </div>
      )
    },
    {
      key: 'order_date',
      header: 'Date/Time',
      accessor: (row) => row.order_date,
      sortable: true,
      render: (value) => (
        <div className="text-xs">
          <div className="text-white">{format(parseISO(value), 'MMM dd, yyyy')}</div>
          <div className="text-gray-500">{format(parseISO(value), 'HH:mm:ss')}</div>
        </div>
      )
    },
    {
      key: 'customer',
      header: 'Customer',
      accessor: (row) => row.customer,
      sortable: true,
      filterable: true,
      render: (value: Customer | undefined) => {
        if (!value) return <span className="text-gray-500">Walk-in</span>

        const name = `${value.first_name || ''} ${value.last_name || ''}`.trim() || value.company_name
        return (
          <div className="flex flex-col">
            <div className="text-white font-medium">{name}</div>
            {value.code && <div className="text-xs text-gray-500">#{value.code}</div>}
            {value.vip_status && <div className="text-xs text-yellow-400">VIP</div>}
          </div>
        )
      }
    },
    {
      key: 'table',
      header: 'Table/Room',
      accessor: (row) => ({ table: row.table, room: row.room }),
      width: '100px',
      render: (value) => {
        if (!value.table) return <span className="text-gray-500">-</span>
        return (
          <div className="flex flex-col">
            <span className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs">
              {value.table.name}
            </span>
            {value.room && <span className="text-xs text-gray-500 mt-1">{value.room.name}</span>}
          </div>
        )
      }
    },
    {
      key: 'order_type',
      header: 'Type',
      accessor: (row) => row.order_type,
      sortable: true,
      width: '80px',
      render: (value) => (
        <StatusBadge
          status={value}
          variants={{
            sale: { color: 'blue', label: 'Sale' },
            return: { color: 'red', label: 'Return' },
            exchange: { color: 'yellow', label: 'Exchange' },
            quote: { color: 'gray', label: 'Quote' },
            refund: { color: 'orange', label: 'Refund' }
          }}
        />
      )
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      sortable: true,
      align: 'center',
      width: '100px',
      render: (value, row) => {
        if (row.is_voided) {
          return <StatusBadge status="voided" color="red" label="Voided" />
        }
        return (
          <StatusBadge
            status={value}
            variants={{
              draft: { color: 'gray', label: 'Draft' },
              open: { color: 'blue', label: 'Open' },
              parked: { color: 'yellow', label: 'Parked' },
              completed: { color: 'green', label: 'Completed' },
              refunded: { color: 'orange', label: 'Refunded' }
            }}
          />
        )
      }
    },
    {
      key: 'payment_status',
      header: 'Payment',
      accessor: (row) => row.payment_status,
      sortable: true,
      align: 'center',
      width: '100px',
      render: (value) => (
        <StatusBadge
          status={value}
          variants={{
            unpaid: { color: 'red', label: 'Unpaid' },
            partial: { color: 'yellow', label: 'Partial' },
            paid: { color: 'green', label: 'Paid' },
            refunded: { color: 'orange', label: 'Refunded' }
          }}
        />
      )
    },
    {
      key: 'guest_count',
      header: 'Guests',
      accessor: (row) => row.guest_count || row.customer_count || 1,
      align: 'center',
      width: '70px',
      render: (value) => (
        <div className="flex items-center justify-center">
          <Users size={14} className="text-gray-400 mr-1" />
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'subtotal',
      header: 'Subtotal',
      accessor: (row) => row.subtotal,
      align: 'right',
      render: (value) => <CurrencyDisplay value={value} />,
      sortable: true
    },
    {
      key: 'discount_amount',
      header: 'Discount',
      accessor: (row) => row.discount_amount,
      align: 'right',
      render: (value) => value > 0 
        ? <span className="text-orange-400">-<CurrencyDisplay value={value} /></span>
        : <span className="text-gray-500">-</span>,
      sortable: true
    },
    {
      key: 'tax_amount',
      header: 'Tax',
      accessor: (row) => row.tax_amount,
      align: 'right',
      render: (value) => <CurrencyDisplay value={value} />,
      sortable: true
    },
    {
      key: 'total_amount',
      header: 'Total',
      accessor: (row) => row.total_amount,
      align: 'right',
      render: (value, row) => (
        <div className="flex flex-col items-end">
          <span className="text-white font-bold"><CurrencyDisplay value={value} /></span>
          {row.delivery_fee > 0 && (
            <span className="text-xs text-gray-500">+<CurrencyDisplay value={row.delivery_fee} /> delivery</span>
          )}
          {row.total_tips > 0 && (
            <span className="text-xs text-green-500">+<CurrencyDisplay value={row.total_tips} /> tip</span>
          )}
        </div>
      ),
      sortable: true,
      className: 'font-bold'
    },
    {
      key: 'delivery_status',
      header: 'Delivery',
      accessor: (row) => row.delivery_status,
      width: '100px',
      render: (value) => value ? (
        <StatusBadge
          status={value}
          variants={{
            pending: { color: 'yellow', label: 'Pending' },
            assigned: { color: 'blue', label: 'Assigned' },
            in_transit: { color: 'purple', label: 'En Route' },
            delivered: { color: 'green', label: 'Delivered' },
            failed: { color: 'red', label: 'Failed' }
          }}
        />
      ) : <span className="text-gray-500">-</span>
    },
    {
      key: 'user',
      header: 'Cashier',
      accessor: (row) => row.user,
      width: '120px',
      render: (value: User | undefined) => value ? (
        <div className="text-sm">
          <div className="text-white">{value.first_name} {value.last_name}</div>
          <div className="text-xs text-gray-500">{value.role}</div>
        </div>
      ) : <span className="text-gray-500">-</span>
    }
  ], [])

  /**
   * Order Items Columns
   */
  const orderItemColumns: Column<OrderItem>[] = useMemo(() => [
    {
      key: 'order_number',
      header: 'Order #',
      accessor: (row) => row.order_id,
      width: '120px',
      className: 'font-mono'
    },
    {
      key: 'line_number',
      header: 'Line',
      accessor: (row) => row.line_number,
      width: '60px',
      align: 'center'
    },
    {
      key: 'product_name',
      header: 'Product',
      accessor: (row) => row.product_name,
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="text-white font-medium">{value}</div>
          {row.product_code && <div className="text-xs text-gray-500">{row.product_code}</div>}
          {row.notes && <div className="text-xs text-blue-400 italic mt-1">{row.notes}</div>}
          {row.allergen_warnings && (
            <div className="text-xs text-red-400 mt-1">
              <AlertTriangle size={12} className="inline mr-1" />
              Allergens: {row.allergen_warnings}
            </div>
          )}
          {row.order_item_modifiers && row.order_item_modifiers.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">
              +{row.order_item_modifiers.length} modifier(s)
            </div>
          )}
        </div>
      )
    },
    {
      key: 'quantity',
      header: 'Qty',
      accessor: (row) => row.quantity,
      align: 'right',
      width: '80px',
      className: 'font-semibold',
      render: (value, row) => (
        <div>
          <div className="text-right">{value.toFixed(3)}</div>
          {row.return_quantity > 0 && (
            <div className="text-xs text-red-400 text-right">
              -{row.return_quantity.toFixed(3)} returned
            </div>
          )}
        </div>
      )
    },
    {
      key: 'unit_price',
      header: 'Price',
      accessor: (row) => row.unit_price,
      align: 'right',
      render: (value) => <CurrencyDisplay value={value} />,
      sortable: true
    },
    {
      key: 'discount',
      header: 'Discount',
      accessor: (row) => row.discount_amount,
      align: 'right',
      render: (value, row) => {
        if (row.discount_percent > 0) {
          return <span className="text-orange-400">{row.discount_percent}%</span>
        }
        if (value > 0) {
          return <span className="text-orange-400">-<CurrencyDisplay value={value} /></span>
        }
        return <span className="text-gray-500">-</span>
      }
    },
    {
      key: 'total_amount',
      header: 'Total',
      accessor: (row) => row.total_amount + (row.modifier_total || 0),
      align: 'right',
      render: (value, row) => (
        <div className="flex flex-col items-end">
          <span className="text-white font-bold"><CurrencyDisplay value={value} /></span>
          {row.modifier_total > 0 && (
            <span className="text-xs text-gray-500">+<CurrencyDisplay value={row.modifier_total} /> modifiers</span>
          )}
        </div>
      ),
      sortable: true,
      className: 'font-bold'
    },
    {
      key: 'round_number',
      header: 'Round',
      accessor: (row) => row.round_number,
      align: 'center',
      width: '70px',
      render: (value) => value > 1 
        ? <span className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs">R{value}</span>
        : <span className="text-gray-500">-</span>
    },
    {
      key: 'preparation_status',
      header: 'Kitchen',
      accessor: (row) => row.preparation_status,
      align: 'center',
      width: '90px',
      render: (value) => value ? (
        <StatusBadge
          status={value}
          variants={{
            pending: { color: 'yellow', label: 'Pending' },
            preparing: { color: 'blue', label: 'Preparing' },
            ready: { color: 'green', label: 'Ready' },
            served: { color: 'purple', label: 'Served' },
            canceled: { color: 'red', label: 'Canceled' }
          }}
        />
      ) : <span className="text-gray-500">-</span>
    },
    {
      key: 'status',
      header: 'Item Status',
      accessor: (row) => row.is_voided ? 'voided' : row.is_canceled ? 'canceled' : row.is_confirmed ? 'confirmed' : 'pending',
      align: 'center',
      render: (value) => (
        <StatusBadge
          status={value}
          variants={{
            confirmed: { color: 'green', label: 'Confirmed' },
            pending: { color: 'yellow', label: 'Pending' },
            canceled: { color: 'red', label: 'Canceled' },
            voided: { color: 'gray', label: 'Voided' }
          }}
        />
      )
    }
  ], [])

  /**
   * Payment Columns
   */
  const paymentColumns: Column<OrderPayment>[] = useMemo(() => [
    {
      key: 'payment_date',
      header: 'Date/Time',
      accessor: (row) => row.payment_date,
      sortable: true,
      render: (value) => (
        <div className="text-xs">
          <div className="text-white">{format(parseISO(value), 'MMM dd, yyyy')}</div>
          <div className="text-gray-500">{format(parseISO(value), 'HH:mm:ss')}</div>
        </div>
      )
    },
    {
      key: 'order_id',
      header: 'Order #',
      accessor: (row) => row.order_id,
      width: '120px',
      className: 'font-mono',
      render: (value) => (
        <Link 
          to={`/app/orders/${value}`}
          className="text-blue-400 hover:text-blue-300 hover:underline font-mono"
        >
          {value}
        </Link>
      )
    },
    {
      key: 'payment_type',
      header: 'Payment Type',
      accessor: (row) => row.payment_type,
      sortable: true,
      render: (value, rowPayment: any) => (
        <div className="flex items-center gap-2">
          <div className={`p-1 rounded ${
            value?.code === 'cash' ? 'bg-green-900/30' :
            value?.code === 'card' ? 'bg-blue-900/30' :
            value?.code === 'credit' ? 'bg-orange-900/30' :
            'bg-gray-700/30'
          }`}>
            {value?.code === 'cash' && <DollarSign size={16} className="text-green-400" />}
            {value?.code === 'card' && <CardIcon size={16} className="text-blue-400" />}
            {value?.code === 'credit' && <Receipt size={16} className="text-orange-400" />}
            {value?.code === 'giftcard' && <Gift size={16} className="text-purple-400" />}
            {!value?.code && <Wallet size={16} className="text-gray-400" />}
          </div>
          <div>
            <div className="text-white">{(value as any)?.name || '-'}</div>
            {(rowPayment as any).card_last_four && (
              <div className="text-xs text-gray-500">**** {(rowPayment as any).card_last_four}</div>
            )}
            {(rowPayment as any).authorization_code && (
              <div className="text-xs text-gray-500">Auth: {(rowPayment as any).authorization_code}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      accessor: (row) => row.amount,
      align: 'right',
      render: (value) => <span className="text-white font-bold"><CurrencyDisplay value={value} /></span>,
      sortable: true,
      className: 'font-bold'
    },
    {
      key: 'tendered_amount',
      header: 'Tendered',
      accessor: (row) => row.tendered_amount,
      align: 'right',
      render: (value) => value ? <CurrencyDisplay value={value} /> : '-'
    },
    {
      key: 'change_amount',
      header: 'Change',
      accessor: (row) => row.change_amount,
      align: 'right',
      render: (value) => value > 0 
        ? <span className="text-yellow-400"><CurrencyDisplay value={value} /></span>
        : <span className="text-gray-500">-</span>
    },
    {
      key: 'reference_number',
      header: 'Reference',
      accessor: (row) => row.reference_number || '-',
      className: 'font-mono text-xs'
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.is_voided ? 'voided' : 'active',
      align: 'center',
      width: '80px',
      render: (value) => (
        <StatusBadge
          status={value}
          variants={{
            active: { color: 'green', label: 'Active' },
            voided: { color: 'red', label: 'Voided' }
          }}
        />
      )
    }
  ], [])

  /**
   * Voided Items Columns
   */
  const voidedItemColumns: Column<VoidedItem>[] = useMemo(() => [
    {
      key: 'voided_date',
      header: 'Voided Date',
      accessor: (row) => row.voided_date,
      sortable: true,
      render: (value) => (
        <div className="text-xs">
          <div className="text-white">{format(parseISO(value), 'MMM dd, yyyy')}</div>
          <div className="text-gray-500">{format(parseISO(value), 'HH:mm:ss')}</div>
        </div>
      )
    },
    {
      key: 'order_id',
      header: 'Order #',
      accessor: (row) => row.order_id,
      width: '120px',
      className: 'font-mono',
      render: (value) => (
        <Link 
          to={`/app/orders/${value}`}
          className="text-blue-400 hover:text-blue-300 hover:underline font-mono"
        >
          {value}
        </Link>
      )
    },
    {
      key: 'product_name',
      header: 'Product',
      accessor: (row) => row.product_name,
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="text-white font-medium">{value}</div>
          {row.product?.code && <div className="text-xs text-gray-500">{row.product.code}</div>}
        </div>
      )
    },
    {
      key: 'quantity',
      header: 'Qty',
      accessor: (row) => row.quantity,
      align: 'right',
      width: '80px',
      className: 'font-semibold'
    },
    {
      key: 'unit_price',
      header: 'Price',
      accessor: (row) => row.unit_price,
      align: 'right',
      render: (value) => <CurrencyDisplay value={value} />
    },
    {
      key: 'total_amount',
      header: 'Total',
      accessor: (row) => row.total_amount,
      align: 'right',
      render: (value) => <span className="text-red-400 font-bold"><CurrencyDisplay value={value} /></span>,
      className: 'font-bold'
    },
    {
      key: 'void_reason',
      header: 'Reason',
      accessor: (row) => row.void_reason_text || row.void_reason?.name,
      render: (value) => (
        <div className="text-sm">
          <div className="text-gray-300">{value || 'No reason provided'}</div>
          <div className="text-xs text-gray-500">Voided</div>
        </div>
      )
    },
    {
      key: 'voided_by',
      header: 'Voided By',
      accessor: (row) => row.voided_by,
      width: '120px'
    }
  ], [])

  const renderContent = () => {
    switch (activeTab) {
      case 'orders':
        return renderOrdersContent()
      case 'items':
        return renderOrderItemsContent()
      case 'payments':
        return renderPaymentsContent()
      case 'rounds':
        return renderRoundsContent()
      case 'voided':
        return renderVoidedItemsContent()
      case 'analytics':
        return renderAnalyticsContent()
      default:
        return null
    }
  }

  const renderOrdersContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Orders', icon: ShoppingCart, color: 'gray' },
      { id: 'draft', label: 'Draft', icon: FileText, color: 'gray' },
      { id: 'open', label: 'Open', icon: Clock, color: 'blue' },
      { id: 'parked', label: 'Parked', icon: AlertCircle, color: 'yellow' },
      { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'green' },
      { id: 'voided', label: 'Voided', icon: XCircle, color: 'red' },
      { id: 'refunded', label: 'Refunded', icon: RotateCcw, color: 'orange' }
    ]

    return (
      <div className="space-y-6">
        {/* Sub Tabs */}
        <div className="flex gap-1 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-400 bg-gray-750`
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Advanced Filters */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Filter size={18} />
              Advanced Filters
            </h3>
            <button
              onClick={() => {
                setFilterOptions({
                  status: '',
                  payment_status: '',
                  order_type: '',
                  date_from: '',
                  date_to: '',
                  customer_id: '',
                  user_id: '',
                  min_amount: '',
                  max_amount: '',
                  delivery_status: '',
                  kitchen_status: '',
                  source_channel: '',
                  sales_channel: ''
                })
                setDateRange([null, null])
                setSearchTerm('')
              }}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select
                value={filterOptions.status}
                onChange={(e) => setFilterOptions({...filterOptions, status: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="open">Open</option>
                <option value="parked">Parked</option>
                <option value="completed">Completed</option>
                <option value="voided">Voided</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Payment</label>
              <select
                value={filterOptions.payment_status}
                onChange={(e) => setFilterOptions({...filterOptions, payment_status: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value="">All Payments</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">From Date</label>
              <input
                type="date"
                value={filterOptions.date_from}
                onChange={(e) => setFilterOptions({...filterOptions, date_from: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">To Date</label>
              <input
                type="date"
                value={filterOptions.date_to}
                onChange={(e) => setFilterOptions({...filterOptions, date_to: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min Amount</label>
              <input
                type="number"
                value={filterOptions.min_amount}
                onChange={(e) => setFilterOptions({...filterOptions, min_amount: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Max Amount</label>
              <input
                type="number"
                value={filterOptions.max_amount}
                onChange={(e) => setFilterOptions({...filterOptions, max_amount: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
          <div className="flex-1 w-full">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search orders by number, customer name, email, phone, table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              className="px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? <LoadingSpinner size="md" /> : <Download size={18} />}
              CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              disabled={exporting}
              className="px-4 py-2.5 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-green-600/30"
            >
              {exporting ? <LoadingSpinner size="md" /> : <Download size={18} />}
              Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              className="px-4 py-2.5 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-600/30"
            >
              {exporting ? <LoadingSpinner size="md" /> : <Download size={18} />}
              PDF
            </button>
            <Link
              to="/app/pos"
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <ShoppingCart size={18} />
              New Order
            </Link>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
          <DataTable
            data={orders}
            columns={orderColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            actions={(row) => (
              <div className="flex items-center justify-center gap-1">
                <button 
                  className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors" 
                  title="View Details"
                  onClick={() => handleViewOrderDetails(row)}
                >
                  <Eye size={18} className="text-blue-400" />
                </button>
                <div className="relative group">
                  <button className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors">
                    <MoreHorizontal size={18} className="text-gray-400" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      <button
                        onClick={() => handlePrintReceipt(row.id, 'customer')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Printer size={14} />
                        Print Customer Receipt
                      </button>
                      <button
                        onClick={() => handlePrintReceipt(row.id, 'kitchen')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Printer size={14} />
                        Print Kitchen Ticket
                      </button>
                      {row.fiscal_receipt_number && (
                        <button
                          onClick={() => handlePrintReceipt(row.id, 'fiscal')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Printer size={14} />
                          Print Fiscal Copy
                        </button>
                      )}
                      <div className="border-t border-gray-700 my-1"></div>
                      {row.customer?.email && (
                        <button
                          onClick={() => handleSendReceipt(row.id, 'email')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Send size={14} />
                          Email Receipt
                        </button>
                      )}
                      <div className="border-t border-gray-700 my-1"></div>
                      {row.status === 'open' && !row.is_voided && (
                        <>
                          <button
                            onClick={() => openConfirmDialog({
                              title: 'Void Order',
                              message: `Are you sure you want to void order ${row.order_number}? This action cannot be undone.`,
                              confirmText: 'Void Order',
                              type: 'danger',
                              onConfirm: () => handleVoidOrder(row.id, 'Manager void')
                            })}
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Ban size={14} />
                            Void Order
                          </button>
                          <button
                            onClick={() => {/* Open refund modal */}}
                            className="w-full text-left px-4 py-2 text-sm text-orange-400 hover:bg-orange-900/20 flex items-center gap-2"
                          >
                            <RotateCcw size={14} />
                            Process Refund
                          </button>
                        </>
                      )}
                      {row.status === 'voided' && (
                        <button
                          onClick={() => openConfirmDialog({
                            title: 'Reopen Order',
                            message: `Are you sure you want to reopen voided order ${row.order_number}?`,
                            confirmText: 'Reopen',
                            onConfirm: () => handleReopenOrder(row.id)
                          })}
                          className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-green-900/20 flex items-center gap-2"
                        >
                          <RotateCw size={14} />
                          Reopen Order
                        </button>
                      )}
                      <div className="border-t border-gray-700 my-1"></div>
                      <Link
                        to={`/app/orders/${row.id}`}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <ExternalLink size={14} />
                        Open in POS
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <ShoppingCart size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg mb-2">No orders found</div>
                <div className="text-gray-500 text-sm mb-6">Try adjusting your filters or search criteria</div>
                <Link
                  to="/app/pos"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ShoppingCart size={18} />
                  Create New Order
                </Link>
              </div>
            }
          />
        </div>

        {/* Pagination */}
        {totalCount > ITEMS_PER_PAGE && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
              onPageChange={setCurrentPage}
              siblingCount={2}
            />
          </div>
        )}
      </div>
    )
  }

  const renderOrderItemsContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Items', icon: Package, color: 'gray' },
      { id: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: 'green' },
      { id: 'pending', label: 'Pending', icon: Clock, color: 'yellow' },
      { id: 'canceled', label: 'Canceled', icon: XCircle, color: 'red' },
      { id: 'voided', label: 'Voided', icon: Ban, color: 'gray' }
    ]

    return (
      <div className="space-y-6">
        <div className="flex gap-1 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-400 bg-gray-750`
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            )
          })}
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
          <DataTable
            data={orderItems}
            columns={orderItemColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => `${row.order_id}-${row.line_number}`}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Package size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No order items found</div>
              </div>
            }
          />
        </div>

        {/* Pagination */}
        {totalCount > ITEMS_PER_PAGE && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
              onPageChange={setCurrentPage}
              siblingCount={2}
            />
          </div>
        )}
      </div>
    )
  }

  const renderPaymentsContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Payments', icon: CreditCard, color: 'gray' },
      { id: 'cash', label: 'Cash', icon: DollarSign, color: 'green' },
      { id: 'card', label: 'Card', icon: CardIcon, color: 'blue' },
      { id: 'credit', label: 'Credit', icon: Receipt, color: 'orange' },
      { id: 'giftcard', label: 'Gift Card', icon: Gift, color: 'purple' },
      { id: 'other', label: 'Other', icon: Wallet, color: 'gray' }
    ]

    return (
      <div className="space-y-6">
        <div className="flex gap-1 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-400 bg-gray-750`
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            )
          })}
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
          <DataTable
            data={orderPayments}
            columns={paymentColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <CreditCard size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No payments found</div>
              </div>
            }
          />
        </div>

        {/* Pagination */}
        {totalCount > ITEMS_PER_PAGE && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
              onPageChange={setCurrentPage}
              siblingCount={2}
            />
          </div>
        )}
      </div>
    )
  }

  const renderRoundsContent = () => (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
        <h3 className="text-white font-semibold text-lg mb-6 flex items-center gap-2">
          <Layers size={20} />
          Order Rounds / Split Billing
        </h3>
        <div className="space-y-4">
          {orderRounds.map(round => (
            <div key={round.id} className="bg-gray-750 rounded-lg p-4 flex items-center justify-between hover:bg-gray-700 transition-colors border border-gray-700">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="px-3 py-1 bg-purple-900 text-purple-200 rounded-full text-sm font-semibold">
                    Round #{round.round_number}
                  </div>
                  <span className="text-gray-400 text-sm">
                    {format(parseISO(round.created_at), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
                <div className="text-gray-300 text-sm">Order: <span className="font-mono text-blue-400">{round.order_id}</span></div>
                {round.order_item_rounds && (
                  <div className="text-gray-400 text-sm mt-1">
                    {round.order_item_rounds.length} item(s) in this round
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  // Navigate to order with round filter
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors flex items-center gap-2"
              >
                <Eye size={16} />
                View Items
              </button>
            </div>
          ))}
          {orderRounds.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Layers size={48} className="mx-auto mb-4 opacity-50" />
              <div className="text-lg">No order rounds found</div>
              <div className="text-sm mt-2">Order rounds are created when splitting bills or adding items in multiple rounds</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderVoidedItemsContent = () => (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
        <DataTable
          data={voidedItems}
          columns={voidedItemColumns}
          loading={loading}
          error={error}
          pageSize={ITEMS_PER_PAGE}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          rowKey={(row) => row.id}
          emptyState={
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <Ban size={48} className="mx-auto text-gray-600 mb-4" />
              <div className="text-gray-400 text-lg">No voided items found</div>
              <div className="text-gray-500 text-sm mt-2">Voided items will appear here once created</div>
            </div>
          }
        />
      </div>

      {/* Pagination */}
      {totalCount > ITEMS_PER_PAGE && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
            onPageChange={setCurrentPage}
            siblingCount={2}
          />
        </div>
      )}
    </div>
  )

  const renderAnalyticsContent = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Total Orders</span>
            <ShoppingCart className="text-blue-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-white mb-2">{analytics.totalOrders.toLocaleString()}</div>
          <div className="text-xs text-gray-500">
            <span className="text-green-400">{analytics.completedOrders} completed</span>
            <span className="mx-2">•</span>
            <span className="text-yellow-400">{analytics.pendingOrders} pending</span>
            <span className="mx-2">•</span>
            <span className="text-red-400">{analytics.voidedOrders} voided</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Total Sales</span>
            <DollarSign className="text-green-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            <CurrencyDisplay value={analytics.totalSales} size="lg" />
          </div>
          <div className="text-xs text-gray-500">
            Avg: <CurrencyDisplay value={analytics.averageOrderValue} />
            <span className="mx-2">•</span>
            <span className="text-orange-400">-<CurrencyDisplay value={analytics.totalDiscount} /> discounts</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Total Tax</span>
            <Receipt className="text-yellow-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            <CurrencyDisplay value={analytics.totalTax} size="lg" />
          </div>
          <div className="text-xs text-gray-500">
            Collected from all orders
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Customers</span>
            <Users className="text-purple-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-white mb-2">{analytics.totalCustomers.toLocaleString()}</div>
          <div className="text-xs text-gray-500">
            <span className="text-green-400">{analytics.newCustomers} new</span>
            <span className="mx-2">•</span>
            <span className="text-blue-400">{analytics.returningCustomers} returning</span>
          </div>
        </div>
      </div>

      {/* Payment Method Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <h3 className="text-white font-semibold text-lg mb-6">Payment Methods Breakdown</h3>
          <div className="space-y-4">
            {analytics.paymentMethodBreakdown.map((method, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    method.payment_type === 'cash' ? 'bg-green-900/30' :
                    method.payment_type === 'card' ? 'bg-blue-900/30' :
                    method.payment_type === 'credit' ? 'bg-orange-900/30' :
                    'bg-gray-700/30'
                  }`}>
                    {method.payment_type === 'cash' && <DollarSign size={20} className="text-green-400" />}
                    {method.payment_type === 'card' && <CardIcon size={20} className="text-blue-400" />}
                    {method.payment_type === 'credit' && <Receipt size={20} className="text-orange-400" />}
                    {method.payment_type === 'giftcard' && <Gift size={20} className="text-purple-400" />}
                  </div>
                  <div>
                    <div className="text-white font-medium capitalize">{method.payment_type}</div>
                    <div className="text-sm text-gray-500">{method.count} transactions</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold"><CurrencyDisplay value={method.total_amount} /></div>
                  <div className="text-sm text-gray-500">{method.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <h3 className="text-white font-semibold text-lg mb-6">Top Products</h3>
          <div className="space-y-4">
            {analytics.topProducts.slice(0, 5).map((product, index) => (
              <div key={product.product_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-700 rounded">
                    <span className="text-gray-400 font-bold">{index + 1}</span>
                  </div>
                  <div className="max-w-[200px]">
                    <div className="text-white font-medium truncate">{product.product_name}</div>
                    <div className="text-sm text-gray-500">{product.quantity.toLocaleString()} sold</div>
                  </div>
                </div>
                <div className="text-white font-bold">
                  <CurrencyDisplay value={product.total_amount} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="text-gray-400 text-sm mb-2">Total Tips</div>
          <div className="text-2xl font-bold text-green-400">
            <CurrencyDisplay value={analytics.totalTips} />
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="text-gray-400 text-sm mb-2">Service Charge</div>
          <div className="text-2xl font-bold text-yellow-400">
            <CurrencyDisplay value={analytics.totalServiceCharge} />
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="text-gray-400 text-sm mb-2">Delivery Fees</div>
          <div className="text-2xl font-bold text-blue-400">
            <CurrencyDisplay value={analytics.totalDeliveryFee} />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950">
        {/* Header */}
        <div className="bg-gray-800/80 backdrop-blur-sm px-6 py-4 flex items-center justify-between border-b border-gray-700/50">
          <div className="flex items-center gap-4">
            <Link 
              to="/app" 
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <ShoppingCart size={22} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Order Management System</h2>
                <div className="text-sm text-gray-500">Comprehensive order tracking and analytics</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
              onClick={fetchData}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={handleMaximize} 
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors" 
              title="Fullscreen"
            >
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50">
          <div className="flex gap-1 px-6 overflow-x-auto">
            {[
              { id: 'orders', label: 'Orders', icon: ShoppingCart, description: 'View and manage all orders' },
              { id: 'items', label: 'Order Items', icon: Package, description: 'Detailed item-level view' },
              { id: 'payments', label: 'Payments', icon: CreditCard, description: 'Payment transaction history' },
              { id: 'rounds', label: 'Rounds', icon: Layers, description: 'Split billing and order rounds' },
              { id: 'voided', label: 'Voided Items', icon: Ban, description: 'Voided items and transactions' },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp, description: 'Sales analytics and insights' }
            ].map(tab => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType)
                    setActiveSubTab('all')
                    setCurrentPage(1)
                  }}
                  className={`group flex flex-col items-start px-4 py-3 border-b-2 transition-all whitespace-nowrap min-w-[120px] ${
                    isActive
                      ? 'border-blue-500 text-blue-400 bg-gray-750'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={18} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </div>
                  <div className={`text-xs transition-all ${
                    isActive ? 'text-blue-300/70' : 'text-gray-500 group-hover:text-gray-400'
                  }`}>
                    {tab.description}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          isOpen={showOrderDetails}
          onClose={() => {
            setShowOrderDetails(false)
            setSelectedOrder(null)
          }}
          onVoid={handleVoidOrder}
          onRefund={handleRefundOrder}
          onPrintReceipt={handlePrintReceipt}
          onSendReceipt={handleSendReceipt}
          onReopen={handleReopenOrder}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        confirmText={confirmDialogProps.confirmText || 'Confirm'}
        cancelText={confirmDialogProps.cancelText || 'Cancel'}
        onConfirm={confirmAction}
        onClose={closeConfirmDialog}
        isLoading={confirmDialogProps.isLoading}
      />
    </ErrorBoundary>
  )
}